import { useEffect, useRef } from "react";
import { createChart } from "lightweight-charts";
import { Panel } from "@/components/common";

const ROWS = [["total_return_pct", "Return %"], ["cagr_pct", "CAGR %"], ["max_drawdown_pct", "Max DD %"], ["sharpe", "Sharpe"], ["trades", "Trades"],
  ["win_rate_pct", "Win %"], ["profit_factor", "Profit factor"], ["avg_r", "Avg R"], ["fees_paid", "Fees"], ["exposure_pct", "Exposure %"], ["final_equity", "Final equity"]];

function EquityLine({ curve }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) { return; }
    const chart = createChart(ref.current, { height: 260, autoSize: true,
      layout: { background: { type: "solid", color: "#121824" }, textColor: "#94A3B8" },
      grid: { vertLines: { color: "#1E293B" }, horzLines: { color: "#1E293B" } }, timeScale: { timeVisible: true } });
    for (const [seg, color] of [["in_sample", "#38BDF8"], ["out_of_sample", "#F59E0B"]]) {
      const s = chart.addLineSeries({ color, lineWidth: 2 });
      s.setData(curve.filter((p) => p.segment === seg).map((p) => ({ time: Math.floor(p.t / 1000), value: Number(p.equity) })));
    }
    chart.timeScale().fitContent();
    return () => { chart.remove(); };
  }, [curve]);
  return <div ref={ref} data-testid="bt-equity-chart" className="w-full" />;
}

export function BacktestResult({ r }) {
  return (
    <Panel title={`Result · ${r.spec.strategy} · ${r.spec.symbol} ${r.spec.interval}`} testId="backtest-result">
      <div className="p-4 space-y-3 text-xs">
        <div data-testid="bt-run-hash" className="font-mono text-slate-500 break-all">run {r.run_hash.slice(0, 16)}… · data {r.dataset_checksum.slice(0, 12)}… · {r.engine_version}{r.reproduced ? " · reproduced" : ""} · gaps {r.data_gaps}</div>
        <EquityLine curve={r.equity_curve} />
        <div className="text-slate-500"><span className="text-sky-400">■</span> in-sample · <span className="text-amber-400">■</span> out-of-sample (separate run, fresh capital, earlier bars used only as indicator warm-up)</div>
        <table className="w-full" data-testid="bt-metrics">
          <thead><tr className="text-slate-500 text-left"><th className="py-1">Metric</th><th>In-sample</th><th>Out-of-sample</th></tr></thead>
          <tbody>{ROWS.map(([k, l]) => (
            <tr key={k} className="border-t border-[var(--border-subtle)] text-slate-300"><td className="py-1">{l}</td>
              <td data-testid={`bt-is-${k}`}>{r.in_sample?.[k] ?? "—"}</td><td data-testid={`bt-oos-${k}`}>{r.out_of_sample?.[k] ?? "—"}</td></tr>))}</tbody>
        </table>
        <div className="text-slate-500">Fills at next bar open + slippage; stop checked before target inside a bar; costs are your inputs, not a broker tariff. Research only — never touches paper or real capital.</div>
      </div>
    </Panel>
  );
}
